/**
 * The editable form of a response, and the conversions to and from what the
 * API stores. Kept apart from the components so the fiddly parts — the body
 * mode, the header rows — can be tested without rendering anything.
 */
import { MAX_DELAY_MS, type MockResponse } from './types.js';

export interface HeaderRow {
  name: string;
  value: string;
}

/**
 * `json` is worth preferring and the editor says so: the server renders a
 * structured body leaf by leaf and serialises it itself, so a templated value
 * can never break out of its slot. `text` is for mocking XML, CSV or a body
 * that is deliberately malformed.
 */
export type BodyMode = 'none' | 'json' | 'text';

export interface ResponseDraft {
  status: string;
  headers: HeaderRow[];
  bodyMode: BodyMode;
  bodyText: string;
  delayMs: string;
}

export const EMPTY_DRAFT: ResponseDraft = {
  status: '200',
  headers: [],
  bodyMode: 'json',
  bodyText: '{\n  "ok": true\n}',
  delayMs: '0',
};

export function toDraft(response: MockResponse): ResponseDraft {
  const headers = Object.entries(response.headers ?? {}).map(([name, value]) => ({ name, value }));
  const body = response.body;

  return {
    status: String(response.status),
    headers,
    delayMs: String(response.delayMs ?? 0),
    ...bodyToDraft(body),
  };
}

function bodyToDraft(body: unknown): Pick<ResponseDraft, 'bodyMode' | 'bodyText'> {
  if (body === undefined || body === null) return { bodyMode: 'none', bodyText: '' };
  if (typeof body === 'string') return { bodyMode: 'text', bodyText: body };
  return { bodyMode: 'json', bodyText: JSON.stringify(body, null, 2) };
}

export class DraftError extends Error {}

/** Turns the form back into a response, or explains what is wrong with it. */
export function fromDraft(draft: ResponseDraft): MockResponse {
  const status = Number(draft.status);
  if (!Number.isInteger(status) || status < 100 || status > 599) {
    throw new DraftError('El status debe ser un entero entre 100 y 599.');
  }

  const delayMs = Number(draft.delayMs === '' ? 0 : draft.delayMs);
  if (!Number.isInteger(delayMs) || delayMs < 0 || delayMs > MAX_DELAY_MS) {
    throw new DraftError(`El retardo debe ir de 0 a ${MAX_DELAY_MS} ms.`);
  }

  const headers: Record<string, string> = {};
  for (const row of draft.headers) {
    const name = row.name.trim();
    if (name === '') continue;
    if (/[\r\n\0]/.test(row.value)) {
      throw new DraftError(`La cabecera "${name}" no puede contener saltos de línea.`);
    }
    headers[name] = row.value;
  }

  const response: MockResponse = { status, headers, delayMs };

  if (draft.bodyMode === 'text') {
    response.body = draft.bodyText;
  } else if (draft.bodyMode === 'json') {
    if (draft.bodyText.trim() === '') throw new DraftError('El cuerpo JSON está vacío.');
    try {
      response.body = JSON.parse(draft.bodyText);
    } catch (error) {
      throw new DraftError(
        `El cuerpo no es JSON válido: ${error instanceof Error ? error.message : 'error'}`,
      );
    }
  }

  return response;
}

/** Moves one item of a list, which is all a drag-and-drop reorder ever does. */
export function move<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) {
    return [...items];
  }

  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}
